import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Json = Database['public']['Tables']['assignment_rules']['Row']['metadata'];

interface AssignmentRuleMetadata {
  selected_user_ids?: string[];
  last_assigned_index?: number;
}

interface AssignmentRule {
  id: string;
  name?: string;
  priority?: number;
  market?: string | null;
  source?: string | null;
  origem?: string | null;
  assignment_strategy: string;
  metadata?: Json;
}

export const useLeadAssignment = () => {
  const getAssignedUserForLead = async (leadData: { market: string; origem?: string; source?: string }) => {
    try {
      // Normalize lead data for case-insensitive comparison
      const normalizedLeadData = {
        market: (leadData.market || '').toUpperCase(),
        origem: (leadData.origem || '').toLowerCase(),
        source: (leadData.source || '').toLowerCase()
      };

      console.log('🔍 Looking for assignment rule with normalized data:', normalizedLeadData);

      // Find matching assignment rule
      const { data: rules, error: rulesError } = await supabase
        .from('assignment_rules')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: false });

      if (rulesError) {
        console.error('❌ Error fetching assignment rules:', rulesError);
        return null;
      }

      if (!rules || rules.length === 0) {
        console.log('⚠️ No active assignment rules found');
        return null;
      }

      // Find best matching rule with case-insensitive comparison
      let matchedRule: AssignmentRule | null = null;
      
      for (const rule of rules) {
        const marketMatch = !rule.market || rule.market.toUpperCase() === normalizedLeadData.market;
        const sourceMatch = !rule.source || rule.source.toLowerCase() === normalizedLeadData.origem || rule.source.toLowerCase() === normalizedLeadData.source;
        const origemMatch = !rule.origem || rule.origem.toLowerCase() === normalizedLeadData.origem || rule.origem.toLowerCase() === normalizedLeadData.source;
        
        if (marketMatch && (sourceMatch || origemMatch)) {
          matchedRule = rule as AssignmentRule;
          console.log('✅ Matched assignment rule:', { id: matchedRule.id, name: matchedRule.name, priority: matchedRule.priority });
          break;
        }
      }

      if (!matchedRule) {
        console.log('⚠️ No matching assignment rule found for lead:', normalizedLeadData);
        return null;
      }

      // Parse metadata safely
      const metadata = (matchedRule.metadata as AssignmentRuleMetadata) || {};
      const userIds = metadata.selected_user_ids || [];
      
      if (userIds.length === 0) {
        console.log('⚠️ No users configured in assignment rule');
        return null;
      }

      console.log('👥 Selected user IDs in rule:', userIds);

      // Get current index (default to -1 so first assignment is index 0)
      const lastIndex = metadata.last_assigned_index ?? -1;
      const nextIndex = (lastIndex + 1) % userIds.length;
      const assignedUserId = userIds[nextIndex];

      console.log('🎯 Round-robin assignment: lastIndex =', lastIndex, '→ nextIndex =', nextIndex, '→ userId =', assignedUserId);

      // Update rule with new index
      const updatedMetadata = {
        ...metadata,
        selected_user_ids: userIds,
        last_assigned_index: nextIndex
      };

      const { error: updateError } = await supabase
        .from('assignment_rules')
        .update({ metadata: updatedMetadata as Json })
        .eq('id', matchedRule.id);

      if (updateError) {
        console.error('❌ Error updating assignment rule:', updateError);
        // Still return the user even if update fails
      }

      // Get user data from app_users
      const { data: userData, error: userError } = await supabase
        .from('app_users')
        .select('id, email, display_name, first_name, last_name')
        .eq('id', assignedUserId)
        .single();

      if (userError) {
        console.error('❌ Error fetching user data:', userError);
        return null;
      }

      const userName = userData?.display_name || `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || userData?.email;
      console.log('✅ Successfully assigned to:', userName, '(UUID:', userData?.id, ')');
      
      return userData?.id || null;

    } catch (error) {
      console.error('Error in getAssignedUserForLead:', error);
      return null;
    }
  };

  return { getAssignedUserForLead };
};
